import {
	collection,
	query,
	where,
	getDocs,
	deleteDoc,
	doc,
} from "firebase/firestore";
import { db } from "../firebase";

export class PreviewService {
	/**
	 * Clean up expired preview guides
	 */
	static async cleanupExpiredPreviews(): Promise<number> {
		try {
			const now = new Date();
			const guidesRef = collection(db, "guides");

			// First get all preview guides
			const previewQuery = query(guidesRef, where("isPreview", "==", true));
			const previewSnapshot = await getDocs(previewQuery);

			// Filter expired ones and delete them
			const expiredGuides = previewSnapshot.docs.filter((doc) => {
				const data = doc.data();
				const expiresAt =
					data.previewExpiresAt?.toDate?.() || data.previewExpiresAt;
				return expiresAt && expiresAt <= now;
			});

			if (expiredGuides.length === 0) {
				return 0;
			}

			const deletePromises = expiredGuides.map((doc) => deleteDoc(doc.ref));
			await Promise.all(deletePromises);

			console.log(`Cleaned up ${expiredGuides.length} expired preview guides`);
			return expiredGuides.length;
		} catch (error) {
			console.error("Error cleaning up expired previews:", error);
			return 0;
		}
	}

	/**
	 * Clean up old preview guides for a specific user
	 */
	static async cleanupUserPreviews(
		userId: string,
		keepLatest: number = 3
	): Promise<number> {
		try {
			const guidesRef = collection(db, "guides");

			// First get all preview guides for this user
			const previewQuery = query(
				guidesRef,
				where("isPreview", "==", true),
				where("authorId", "==", userId)
			);
			const snapshot = await getDocs(previewQuery);
			const docs = snapshot.docs;

			if (docs.length <= keepLatest) {
				return 0; // No cleanup needed
			}

			// Sort by creation date, keep the latest ones
			const sortedDocs = docs
				.map((doc) => ({
					id: doc.id,
					...doc.data(),
					createdAt:
						doc.data().createdAt?.toDate?.() ||
						doc.data().createdAt ||
						new Date(0),
				}))
				.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

			// Delete older previews, keep the latest ones
			const toDelete = sortedDocs.slice(keepLatest);
			const deletePromises = toDelete.map((doc) =>
				deleteDoc(doc(db, "guides", doc.id))
			);

			await Promise.all(deletePromises);

			console.log(
				`Cleaned up ${toDelete.length} old preview guides for user ${userId}`
			);
			return toDelete.length;
		} catch (error) {
			console.error("Error cleaning up user previews:", error);
			return 0;
		}
	}

	/**
	 * Check if a guide is a valid preview for the current user
	 */
	static async validatePreviewAccess(
		guideId: string,
		userId: string,
		isAdmin: boolean
	): Promise<{
		valid: boolean;
		guide?: any;
		error?: string;
	}> {
		try {
			console.log("Validating preview access:", { guideId, userId, isAdmin });
			// Get the guide document directly
			const guideRef = doc(db, "guides", guideId);
			const guideSnap = await getDoc(guideRef);

			if (!guideSnap.exists()) {
				console.log("Guide not found");
				return { valid: false, error: "Guide not found" };
			}

			const guideData = guideSnap.data();
			console.log("Guide data:", guideData);

			if (!guideData.isPreview) {
				console.log("Not a preview guide");
				return { valid: false, error: "Not a preview guide" };
			}

			const now = new Date();
			let expiresAt;
			try {
				expiresAt =
					guideData.previewExpiresAt?.toDate?.() || guideData.previewExpiresAt;
				console.log("Expires at:", expiresAt, "Now:", now);
			} catch (error) {
				console.error("Error parsing expiresAt:", error);
				expiresAt = null;
			}

			if (expiresAt && now > expiresAt) {
				console.log("Preview expired, deleting");
				// Delete expired preview
				await deleteDoc(guideRef);
				return { valid: false, error: "Preview expired" };
			}

			console.log("Checking access:", {
				guideAuthor: guideData.authorId,
				userId,
				isAdmin,
			});
			if (guideData.authorId !== userId && !isAdmin) {
				console.log("Access denied");
				return { valid: false, error: "Access denied" };
			}

			console.log("Access granted");
			return {
				valid: true,
				guide: {
					id: guideSnap.id,
					...guideData,
					createdAt: guideData.createdAt?.toDate?.() || guideData.createdAt,
					previewExpiresAt: expiresAt,
				},
			};
		} catch (error) {
			console.error("Error validating preview access:", error);
			return { valid: false, error: "Validation error" };
		}
	}
}
